import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { runDiagnostics, DiagnosticReport } from '@/services/diagnostics';
import { sanitizeDiagnosticText, SanitizeResult } from '@/services/diagnosticSanitizer';
import { getStatusMeta } from '@/config/statuses.config';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { AI_PROVIDERS } from '@/config/aiProviders.config';
import { ENV } from '@/config/env';
import { DATA_MODE } from '@/config/dataMode';
import { SystemService, ActivityEvent } from '@/types/entities';

type ConnectorRow = { key: string; displayName: string; category: string; status: string };

/**
 * SUPPORT & DIAGNOSTICS — Phase D §14–19. Every number here is real local
 * state; "Run Diagnostics" logs a real Agent Log entry via
 * dataService.logDiagnosticsRun. The Support Assistant never sends
 * anything anywhere — it only runs typed text through the same
 * sanitizeDiagnosticText() boundary a future AI escalation would have to
 * pass through, and always shows the honest "requires secure provider
 * integration" message instead of fabricating an AI reply.
 */
export function SupportPage() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const [services, setServices] = useState<SystemService[]>([]);
  const [connectors, setConnectors] = useState<ConnectorRow[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [running, setRunning] = useState(false);
  const [assistantText, setAssistantText] = useState('');
  const [assistantResult, setAssistantResult] = useState<SanitizeResult | null>(null);

  function reload() {
    dataService.listSystemServices().then(setServices);
    dataService.listConnectorStatuses().then(setConnectors);
    dataService.listActivity().then(setActivity);
  }
  useEffect(reload, []);

  async function runNow() {
    setRunning(true);
    const theme = document.documentElement.getAttribute('data-theme') ?? 'light';
    const result = await runDiagnostics({ route: location.pathname, lang, theme });
    setReport(result);
    await dataService.logDiagnosticsRun(result.issueCount);
    setRunning(false);
    reload();
  }

  function runAssistant() {
    if (!assistantText.trim()) return;
    setAssistantResult(sanitizeDiagnosticText(assistantText));
  }

  const recentErrors = [...activity].filter((a) => a.result === 'crit').sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, 10);
  const connectorProblems = connectors.filter((c) => {
    const tone = getStatusMeta('connector', c.status).tone;
    return tone === 'crit' || tone === 'warn';
  });
  const aiProviderEntries = Object.values(AI_PROVIDERS);

  return (
    <div>
      <h1>{t('nav.support')}</h1>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>{t('support.subtitle')}</p>

      <div className="overview-section-label">{t('support.systemHealth')}</div>
      <div className="card" style={{ padding: 8, marginBottom: 16 }}>
        <div className="table-scroll">
          <table className="dt">
            <thead><tr><th>{t('support.col.service')}</th><th>{t('support.col.status')}</th><th>{t('support.col.detail')}</th></tr></thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.key}>
                  <td>{s.displayName}</td>
                  <td><StatusBadge entity="system" statusKey={s.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overview-section-label">{t('support.diagnostics')}</div>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <button className="btn-primary" disabled={running} onClick={runNow}>{running ? t('support.running') : t('support.runDiagnostics')}</button>
        {report && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
              {t('support.issuesFound')}: {report.issueCount}
            </div>
            {report.rows.map((r, i) => (
              <div key={i} className="overview-row" style={{ cursor: 'default' }}>
                <span>{t(r.labelKey)}{r.labelSuffix ? ` — ${r.labelSuffix}` : ''}</span>
                <span className={`badge ${r.tone}`}>{r.valueIsKey ? t(r.value) : r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overview-section-label">{t('support.recentErrors')}</div>
      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        {recentErrors.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('support.noErrors')}</div>
        ) : (
          recentErrors.map((a) => (
            <div key={a.id} className="overview-row" style={{ cursor: 'default' }}>
              <span>{a.actorLabel} — {a.action}</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{a.createdAt}</span>
            </div>
          ))
        )}
      </div>

      <div className="overview-section-label">{t('support.connectorProblems')}</div>
      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        {connectorProblems.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('support.noConnectorProblems')}</div>
        ) : (
          connectorProblems.map((c) => (
            <div key={c.key} className="overview-row" style={{ cursor: 'default' }}>
              <span>{c.displayName}</span>
              <StatusBadge entity="connector" statusKey={c.status} />
            </div>
          ))
        )}
      </div>

      <div className="overview-section-label">{t('support.aiProviderProblems')}</div>
      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        {aiProviderEntries.every((p) => !p.configured) && (
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 8 }}>{t('support.noAiProvidersConnected')}</div>
        )}
        {aiProviderEntries.map((p) => (
          <div key={p.key} className="overview-row" style={{ cursor: 'default' }}>
            <span>{p.displayName}</span>
            <span className={`badge ${p.configured ? 'good' : 'neutral'}`}>{p.configured ? t('aiModels.configured') : t('common.notConnected')}</span>
          </div>
        ))}
      </div>

      <div className="overview-section-label">{t('support.appInfo')}</div>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, fontSize: 13 }}>
          <div><div style={{ color: 'var(--text-3)' }}>{t('support.col.version')}</div><div style={{ fontWeight: 700 }}>{ENV.version}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>{t('settingsFields.environment')}</div><div style={{ fontWeight: 700 }}>{t(`env.${ENV.environment}`)}</div></div>
          <div><div style={{ color: 'var(--text-3)' }}>{t('settingsFields.dataMode')}</div><div style={{ fontWeight: 700 }}>{DATA_MODE}</div></div>
        </div>
      </div>

      <div className="overview-section-label">{t('support.supportAssistant')}</div>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <textarea
          className="text-input"
          rows={4}
          placeholder={t('support.assistantPlaceholder')}
          value={assistantText}
          onChange={(e) => setAssistantText(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <button className="btn-primary" disabled={!assistantText.trim()} onClick={runAssistant}>{t('support.assistantRun')}</button>
        {assistantResult && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{t('support.assistantSanitized')}</div>
            <div className="card" style={{ padding: 10, fontSize: 13, whiteSpace: 'pre-wrap' }}>{assistantResult.sanitized}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
              {assistantResult.redactedCount > 0
                ? `${t('support.assistantRedactedCount')}: ${assistantResult.redactedCount} (${assistantResult.redactedLabels.join(', ')})`
                : t('support.assistantNoRedactions')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 10 }}>{t('support.assistantHonestNote')}</div>
          </div>
        )}
      </div>

      <div className="overview-section-label">{t('support.recoveryGuidance')}</div>
      <div className="card" style={{ padding: 16 }}>
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13, color: 'var(--text-2)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>{t('support.recoverySteps.reload')}</li>
          <li>{t('support.recoverySteps.checkConnectors')}</li>
          <li>{t('support.recoverySteps.checkSystem')}</li>
          <li>{t('support.recoverySteps.resetDemo')}</li>
        </ul>
      </div>
    </div>
  );
}
